import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('visible'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

export default function SellingPage() {
  const heroImg = useReveal(0);
  const homeValueCard = useReveal(0);
  const advantageHeading = useReveal(0);
  const advCard0 = useReveal(0);
  const advCard1 = useReveal(120);
  const advCard2 = useReveal(240);
  const advCardRefs = [advCard0, advCard1, advCard2];
  const specCard0 = useReveal(0);
  const specCard1 = useReveal(150);
  const specCardRefs = [specCard0, specCard1];
  const processHeading = useReveal(0);
  const step0 = useReveal(0);
  const step1 = useReveal(80);
  const step2 = useReveal(160);
  const step3 = useReveal(240);
  const step4 = useReveal(320);
  const step5 = useReveal(400);
  const step6 = useReveal(480);
  const stepRefs = [step0, step1, step2, step3, step4, step5, step6];
  const ctaBlock = useReveal(0);

  const steps = [
    { num: '1', title: 'Consultation', desc: "We'll discuss your goals, timeline, and home condition." },
    { num: '2', title: 'Valuation', desc: "Detailed market analysis and data-driven pricing strategy." },
    { num: '3', title: 'Preparation', desc: "I'll recommend improvements. My inspection background helps prioritize what buyers care about most." },
    { num: '4', title: 'Marketing', desc: "Professional photos, MLS, social media, and local outreach." },
    { num: '5', title: 'Showings', desc: "I'll manage tours and gather feedback to attract serious buyers." },
    { num: '6', title: 'Negotiation', desc: "I'll present offers and negotiate the best price, terms, and timeline." },
    { num: '7', title: 'Closing', desc: "I'll coordinate inspections, appraisals, and paperwork to the finish line." },
  ];

  const advCards = [
    { icon: 'ri-search-eye-line', title: 'Inspection Insights', desc: "I'll help you identify and address issues before buyers do, maximizing your sale price and reducing negotiation surprises." },
    { icon: 'ri-price-tag-3-line', title: 'Strategic Pricing', desc: "Data-driven pricing that attracts buyers and gets you top dollar. I'll analyze the market to position your home competitively." },
    { icon: 'ri-megaphone-line', title: 'Targeted Marketing', desc: "Professional photos, online listings, social media promotion, and local outreach to get your home in front of qualified buyers." },
  ];

  const specCards = [
    { icon: 'ri-arrow-left-right-line', title: 'Buying and Selling at the Same Time?', desc: "I'll coordinate timing, contingencies, and logistics so you can move smoothly from one home to the next. No stress, no gaps, no double moves.", cta: 'Learn More' },
    { icon: 'ri-heart-line', title: 'Estate and Probate Support', desc: "Selling a family home or inherited property? I'll handle the details with care and respect, making a difficult process easier for you and your family.", cta: 'Discuss Your Situation' },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F7] page-enter">

      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-14 sm:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-14 items-center">
            <div ref={heroImg} className="reveal from-left rounded-lg overflow-hidden">
              <img src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/28f027266c089ed78e682f583142580d.jpeg" alt="Sold home" className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover object-top" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 animate-fade-in delay-100 font-medium">Seller Services</p>
              <h1 className="font-cormorant text-4xl sm:text-6xl lg:text-7xl font-light text-[#111111] mb-6 animate-fade-in-up delay-200 leading-tight">
                Sell Smarter.<br /><em className="not-italic font-semibold">Sell for More.</em>
              </h1>
              <p className="text-[#555] mb-8 leading-relaxed animate-fade-in-up delay-300">
                Get a complimentary home value analysis and a marketing plan that highlights what makes your property special.
              </p>
              <Link to="/contact" className="inline-block px-7 sm:px-8 py-3.5 sm:py-4 bg-[#1C3A6B] text-white text-sm font-semibold rounded-md hover:bg-[#152E55] transition-all whitespace-nowrap hover-lift animate-fade-in-up delay-400">
                Request Your Home Value
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Home Value */}
      <section className="py-14 sm:py-20 bg-[#FAF9F7]">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <div ref={homeValueCard} className="reveal scale bg-white rounded-lg p-8 sm:p-12 border border-gray-100 hover-lift">
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
              <i className="ri-home-heart-line text-5xl text-[#1C3A6B]"></i>
            </div>
            <h2 className="font-cormorant text-3xl sm:text-4xl font-light text-[#111111] mb-5">What's Your Home Worth?</h2>
            <div className="w-10 h-0.5 bg-[#C8923A] mx-auto mb-6"></div>
            <p className="text-[#666] mb-8 leading-relaxed">Get a free, no-obligation analysis of your home's current market value. I'll review recent sales, market trends, and your home's unique features to give you an accurate estimate.</p>
            <Link to="/contact" className="inline-block px-7 sm:px-8 py-3.5 sm:py-4 bg-[#C8923A] text-white text-sm font-semibold rounded-md hover:bg-[#B07B2A] transition-all whitespace-nowrap hover-lift">
              Request Your Free Analysis
            </Link>
          </div>
        </div>
      </section>

      {/* Advantage */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div ref={advantageHeading} className="reveal text-center mb-10 sm:mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Why Sellers Choose Me</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111]">The Move With Kendrick Advantage</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {advCards.map((item, i) => (
              <div key={i} ref={advCardRefs[i]} className="reveal scale bg-[#FAF9F7] rounded-lg p-7 sm:p-8 text-center hover-lift border border-gray-100">
                <div className="w-14 h-14 mx-auto mb-6 flex items-center justify-center">
                  <i className={`${item.icon} text-4xl text-[#1C3A6B]`}></i>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-[#111111] mb-3">{item.title}</h3>
                <div className="w-8 h-0.5 bg-[#C8923A] mx-auto mb-4"></div>
                <p className="text-sm text-[#666] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized */}
      <section className="py-14 sm:py-20 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            {specCards.map((item, i) => (
              <div key={i} ref={specCardRefs[i]} className="reveal from-left bg-white rounded-lg p-8 sm:p-10 hover-lift border border-gray-100">
                <div className="w-14 h-14 mb-6 flex items-center justify-center">
                  <i className={`${item.icon} text-4xl text-[#1C3A6B]`}></i>
                </div>
                <h3 className="font-cormorant text-xl sm:text-2xl font-semibold text-[#111111] mb-4">{item.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed mb-6">{item.desc}</p>
                <Link to="/contact" className="text-sm font-semibold text-[#1C3A6B] hover:text-[#C8923A] transition-colors inline-flex items-center gap-2 whitespace-nowrap">
                  {item.cta} <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6">
          <div ref={processHeading} className="reveal text-center mb-10 sm:mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">The Process</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111]">How It Works</h2>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {steps.map((step, i) => (
              <div key={i} ref={stepRefs[i]} className="reveal flex gap-4 sm:gap-5 items-start hover-lift bg-[#FAF9F7] rounded-lg p-4 sm:p-5 border border-gray-100">
                <div className="w-10 h-10 sm:w-11 sm:h-11 flex-shrink-0 bg-[#1C3A6B] text-white rounded-full flex items-center justify-center font-cormorant text-lg sm:text-xl font-semibold">
                  {step.num}
                </div>
                <div>
                  <h3 className="font-semibold text-[#111111] mb-1">{step.title}</h3>
                  <p className="text-sm text-[#666]">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-20 bg-[#111111]">
        <div ref={ctaBlock} className="reveal max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-white mb-6">Ready to Sell?</h2>
          <p className="text-white/70 mb-8 sm:mb-10">Let's get your home sold for the right price.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href="tel:+15176629155" className="px-7 sm:px-8 py-3.5 sm:py-4 bg-[#C8923A] text-white font-semibold text-sm rounded-md hover:bg-[#B07B2A] transition-all whitespace-nowrap inline-flex items-center justify-center gap-2 hover-lift">
              <i className="ri-phone-line"></i> Call Kendrick
            </a>
            <Link to="/contact" className="px-7 sm:px-8 py-3.5 sm:py-4 border border-white/30 text-white font-semibold text-sm rounded-md hover:bg-white/10 transition-all whitespace-nowrap hover-lift">
              Request Home Value
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
